import React, { useState, useEffect, useMemo } from 'react';
import { T, VISIBLE_BACKTEST_STRATEGIES } from '../constants';
import { authFetch } from '../utils/api';
import API_URL from '../utils/api';

/* ─────────────────────────────────────────────────────────────────────────────
 * Backtest — visualizes /strategies/api/backtest/reports/
 *
 * One dropdown to pick a strategy (PADÀ / GBEMI), one panel that renders the
 * stored JSON's headline + scenarios + per-book + reconciliation, plus a
 * "Download .docx" button that pulls the locked Word file.
 *
 * Defaults to whichever strategy has the most recent generated_at timestamp,
 * so traders open the page and immediately see fresh results.
 * ──────────────────────────────────────────────────────────────────────────── */

const LIST_URL = '/strategies/api/backtest/reports/';
const FILE_URL = (name) => `/strategies/api/backtest/reports/${name}/`;
const RUN_URL  = '/strategies/api/backtest/run/';

// Cert universe defaults (PADA's picks span 2025-03-10 -> 2026-05-04, but the
// Director wants the GBEMI generation window which is 2024-01-04 09:30 ->
// 2026-05-04 11:24).
const DEFAULT_START = '2024-01-04T09:30';
const DEFAULT_END   = '2026-05-04T11:24';

const fmt = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString('en-US'));
const fmt1 = (n) => (n === null || n === undefined ? '—' : Number(n).toFixed(1));
const fmt2 = (n) => (n === null || n === undefined ? '—' : Number(n).toFixed(2));
const fmt4 = (n) => (n === null || n === undefined ? '—' : Number(n).toFixed(4));


export default function Backtest() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);   // strategy key, e.g. "PADA"
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastFetched, setLastFetched] = useState(null);

  // Window picker — defaults match the cert generation universe
  const [startDt, setStartDt] = useState(DEFAULT_START);
  const [endDt,   setEndDt]   = useState(DEFAULT_END);
  const [running, setRunning] = useState(false);
  const [runMsg,  setRunMsg]  = useState(null);

  const fetchList = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await authFetch(LIST_URL);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      // Filter to the Director-approved subset. Backend lists every report it
      // finds on disk (PADA, GBEMI variants, STRATA, …); the UI shows only
      // those in VISIBLE_BACKTEST_STRATEGIES.
      const allow = new Set(VISIBLE_BACKTEST_STRATEGIES);
      const list = (data.items || []).filter(r => allow.has(r.strategy));
      setItems(list);
      // Default selection: the most recently generated report.
      if (!selected && list.length > 0) {
        const sorted = [...list].sort((a, b) => {
          const ta = a.generated_at || '';
          const tb = b.generated_at || '';
          return tb.localeCompare(ta);
        });
        const first = sorted.find((r) => r.headline) || sorted[0];
        setSelected(first?.strategy || null);
      }
      setLastFetched(new Date());
    } catch (e) {
      setError(e?.message || 'fetch failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const current = useMemo(
    () => items.find((r) => r.strategy === selected) || null,
    [items, selected],
  );

  // POST /run/ — re-runs the harness for the picked strategy + window, then
  // refreshes the report inline.
  const runBacktest = async () => {
    if (!selected) {
      setRunMsg({ kind: 'err', text: 'Pick a strategy first.' });
      return;
    }
    if (!startDt || !endDt || startDt >= endDt) {
      setRunMsg({ kind: 'err', text: 'Start must be strictly before end.' });
      return;
    }
    setRunning(true);
    setRunMsg({ kind: 'info', text: `Running ${selected} backtest — typically 20–60s…` });
    try {
      // HTML datetime-local gives "YYYY-MM-DDTHH:MM"; the harness accepts
      // either "T" or " " between date and time.
      const start = startDt.replace('T', ' ');
      const end   = endDt.replace('T', ' ');
      const res = await authFetch(RUN_URL, {
        method: 'POST',
        body: JSON.stringify({ strategy: selected, start, end }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        const msg = data.error || `HTTP ${res.status}`;
        setRunMsg({ kind: 'err', text: `Run failed: ${msg}` });
        return;
      }
      // Splice the fresh report into items[]. Backend returns the full
      // payload so we don't need a second fetch round-trip.
      const fresh = data.report || {};
      setItems((prev) => {
        const out = prev.map((r) => r.strategy === selected ? {
          ...r,
          ...fresh,
          headline:        fresh.headline,
          scenarios:       fresh.scenarios,
          per_book:        fresh.per_book,
          reconciliation:  fresh.reconciliation,
          backtest_run:    fresh.backtest_run,
          cert_summary:    fresh.cert_summary,
          grade_summary:   fresh.grade_summary,
          trades_research:   fresh.trades_research || [],
          trades_deployable: fresh.trades_deployable || [],
          generated_at:    fresh.generated_at,
        } : r);
        return out;
      });
      const trN = (fresh.trades_research || []).length;
      setRunMsg({
        kind: 'ok',
        text: `Done — ${selected} ${start} → ${end}: ${trN} research trades.`,
      });
    } catch (e) {
      setRunMsg({ kind: 'err', text: e?.message || 'fetch failed' });
    } finally {
      setRunning(false);
    }
  };

  const downloadDocx = (name) => {
    if (!name) return;
    // .docx is a binary file — open in a new tab and let the backend force
    // the download via Content-Disposition: attachment.
    const token = localStorage.getItem('token') || '';
    const url = `${API_URL}${FILE_URL(name)}`;
    // FileResponse on the backend already sets Content-Disposition. We open
    // through fetch + blob so we can carry the auth token through.
    authFetch(FILE_URL(name)).then(async (res) => {
      if (!res.ok) {
        alert(`Download failed: HTTP ${res.status}`);
        return;
      }
      const blob = await res.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = name;
      link.click();
      setTimeout(() => URL.revokeObjectURL(link.href), 5000);
    }).catch((e) => alert(`Download failed: ${e}`));
  };

  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <h1 style={styles.title}>Backtest Reports</h1>
        <span style={styles.subtitle}>
          PADÀ + GBEMI historical replay (matches the cert)
        </span>
      </header>

      <section style={styles.controls}>
        <label style={styles.label}>
          Strategy:
          <select
            value={selected || ''}
            onChange={(e) => setSelected(e.target.value || null)}
            style={styles.select}
            disabled={items.length === 0}
          >
            <option value="" disabled>— pick a strategy —</option>
            {items.map((r) => (
              <option key={r.strategy} value={r.strategy}>
                {r.strategy === 'PADA' ? 'PADÀ (mean-reversion long)'
                  : r.strategy === 'GBEMI' ? 'GBEMI (momentum, long + short + lux)'
                  : r.strategy === 'STRATA_PORTFOLIO_V110' ? 'STRATA-GBEMI Portfolio v1.10 (cert A6E026BF — short stop −70 + D-short guardrail)'
                  : r.strategy === 'STRATA_PORTFOLIO_V16' ? 'STRATA-GBEMI Portfolio v1.6 (cert 48901AD6 — superseded by v1.10)'
                  : r.strategy === 'KUNBI' ? 'KUNBI (MW 1-min long+short · 3-min filter + asymmetric ATR exits · cert EA615CBE)'
                  : r.strategy}
                {r.headline ? ` — ${r.headline.n} trades, ${(r.headline.wr_pct||0).toFixed(1)}% WR` : ' — no report'}
              </option>
            ))}
          </select>
        </label>

        <button onClick={fetchList} disabled={loading} style={styles.refreshBtn}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>

        {lastFetched && (
          <span style={styles.lastFetched}>
            Last fetched: {lastFetched.toLocaleTimeString()}
          </span>
        )}

        {error && <span style={styles.error}>Error: {error}</span>}
      </section>

      <section style={styles.runControls}>
        <span style={styles.runLabel}>Re-run backtest with window:</span>
        <label style={styles.label}>
          Start
          <input
            type="datetime-local"
            value={startDt}
            onChange={(e) => setStartDt(e.target.value)}
            style={styles.dtInput}
            disabled={running}
          />
        </label>
        <label style={styles.label}>
          End
          <input
            type="datetime-local"
            value={endDt}
            onChange={(e) => setEndDt(e.target.value)}
            style={styles.dtInput}
            disabled={running}
          />
        </label>
        <button
          onClick={() => { setStartDt(DEFAULT_START); setEndDt(DEFAULT_END); }}
          disabled={running}
          style={styles.csvBtn}
          title="Reset to the cert universe (2024-01-04 09:30 → 2026-05-04 11:24)"
        >
          ↺ Defaults
        </button>
        <button
          onClick={runBacktest}
          disabled={running || !selected}
          style={running ? styles.runBtnRunning : styles.runBtn}
          title={selected ? `Re-run the ${selected} harness over the chosen window` : 'Pick a strategy first'}
        >
          {running ? `Running ${selected}…` : `▶ Run ${selected || 'backtest'}`}
        </button>
        {runMsg && (
          <span style={{
            fontSize: 11,
            color: runMsg.kind === 'err' ? T.bear : runMsg.kind === 'ok' ? T.bull : T.textDim,
          }}>
            {runMsg.text}
          </span>
        )}
      </section>

      {current ? (
        <ReportPanel report={current} onDownload={downloadDocx} />
      ) : !loading && (
        <div style={styles.empty}>
          {items.length === 0
            ? 'No backtest reports found. Run the harness on the server first.'
            : 'Pick a strategy from the dropdown above.'}
        </div>
      )}
    </div>
  );
}


function ResponsiveTable({ children, minWidth = 680 }) {
  return (
    <div style={styles.tableScroll}>
      <table style={{ ...styles.table, minWidth }}>
        {children}
      </table>
    </div>
  );
}


function ReportPanel({ report, onDownload }) {
  const h = report.headline;
  const recon = report.reconciliation;
  const run = report.backtest_run;
  const cert = report.cert_summary;
  return (
    <>
      <section style={styles.headerCard}>
        <div>
          <div style={styles.strategyTag}>
            {report.strategy === 'PADA' ? 'PADÀ'
              : report.strategy === 'STRATA_PORTFOLIO_V110' ? 'STRATA-GBEMI · Portfolio v1.10'
              : report.strategy === 'STRATA_PORTFOLIO_V16' ? 'STRATA-GBEMI · Portfolio v1.6'
              : report.strategy}
          </div>
          <div style={styles.cardTitle}>
            {report.strategy === 'PADA'
              ? 'Mean-reversion long — pure-price (z + RSI), flat 1c'
              : report.strategy === 'STRATA_PORTFOLIO_V110'
              ? 'GBEMI engine (side-aware stops: −60 long / −70 short) + v1.9 extension layer + STRATA classifier'
              : report.strategy === 'STRATA_PORTFOLIO_V16'
              ? 'STRATA v1.5 + GBEMI v1.3 combined — 6-tier, dedup, 9 cumulative rules'
              : report.strategy === 'KUNBI'
              ? 'Both-directions intraday — MW 1-min trigger + 3-min filter; LONG fixed bracket / SHORT ATR trail; EV-tiered A/B/C with MACD C-skip'
              : 'Momentum (long + short + Lux confirm), graded sizing'}
          </div>
          <div style={styles.metaLine}>
            Generated: <span style={styles.mono}>{report.generated_at || '—'}</span>
          </div>
          {run?.nq_first_bar && (
            <div style={styles.metaLine}>
              Data window: <span style={styles.mono}>{run.nq_first_bar}</span>
              {' → '}
              <span style={styles.mono}>{run.nq_last_bar}</span>
              {' (data_source = '}{run.data_source || 'csv'}{')'}
            </div>
          )}
        </div>
        <div>
          <button
            onClick={() => onDownload(report.docx)}
            disabled={!report.docx}
            style={styles.downloadBtn}
          >
            ↓ Download .docx
          </button>
        </div>
      </section>

      <div style={styles.contextNote}>
        Historical replay over the data window above — these numbers are settled facts,
        not today's live performance. For today's live decisions see the
        <strong style={{ color: T.text }}> Health</strong> page.
      </div>

      {h && (
        <>
          <h2 style={styles.sectionH}>Headline stats vs Certificate</h2>
          <ResponsiveTable minWidth={560}>
            <colgroup>
              <col style={{ width: '52%' }} />
              <col style={{ width: '24%' }} />
              <col style={{ width: '24%' }} />
            </colgroup>
            <thead>
              <tr>
                <th style={styles.th}>Metric</th>
                <th style={styles.thNum}>Backtest</th>
                <th style={styles.thNum}>Cert</th>
              </tr>
            </thead>
            <tbody>
              <Tr k="Trades (n)"        v={fmt(h.n)}           cert={cert?.cert_n ?? cert?.cert_n_combined} />
              <Tr k="Win rate %"        v={fmt1(h.wr_pct)}     cert={cert?.cert_wr_pct ?? cert?.cert_wr_pct_combined} />
              <Tr k="Mean P&L (bp)"     v={fmt2(h.mean_bp)}    cert={cert?.cert_mean_bp ?? cert?.cert_mean_bp_combined} />
              <Tr k="Net @ 2 bp"        v={fmt2(h.net_2bp)}    cert={cert?.cert_net_2bp ?? cert?.cert_net_2bp_combined} />
              <Tr k="95% CI mean (bp)"  v={`[${fmt2(h.ci_lo)}, ${fmt2(h.ci_hi)}]`} cert={cert?.cert_ci} />
              <Tr k="Profit factor"     v={fmt2(h.profit_factor)} />
              <Tr k="Max drawdown (bp)" v={fmt1(h.max_dd_bp)} />
              <Tr k="Trades / week"     v={fmt1(h.trades_per_week)} cert={cert?.cert_trades_per_week} />
            </tbody>
          </ResponsiveTable>
        </>
      )}

      {Array.isArray(report.scenarios) && report.scenarios.length > 0 && (
        <>
          <h2 style={styles.sectionH}>Scenario comparison</h2>
          <ResponsiveTable minWidth={760}>
            <colgroup>
              <col style={{ width: '46%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '11%' }} />
            </colgroup>
            <thead>
              <tr>
                <th style={styles.th}>Scenario</th>
                <th style={styles.thNum}>n</th>
                <th style={styles.thNum}>WR %</th>
                <th style={styles.thNum}>Mean bp</th>
                <th style={styles.thNum}>Net@2bp</th>
                <th style={styles.thNum}>Trades/wk</th>
              </tr>
            </thead>
            <tbody>
              {report.scenarios.map((s, i) => (
                <tr key={i}>
                  <td style={styles.td}>{s.label}</td>
                  <td style={styles.tdNum}>{fmt(s.n)}</td>
                  <td style={styles.tdNum}>{fmt1(s.wr_pct)}</td>
                  <td style={styles.tdNum}>{fmt2(s.mean_bp)}</td>
                  <td style={styles.tdNum}>{fmt2(s.net_2bp)}</td>
                  <td style={styles.tdNum}>{fmt1(s.trades_per_week)}</td>
                </tr>
              ))}
            </tbody>
          </ResponsiveTable>
        </>
      )}

      {Array.isArray(report.grade_summary) && report.grade_summary.length > 0 && (
        <>
          <h2 style={styles.sectionH}>Grade overlay (LOCKED_Summary style)</h2>
          <ResponsiveTable minWidth={820}>
            <colgroup>
              <col style={{ width: '20%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '13%' }} />
              <col style={{ width: '18%' }} />
              <col style={{ width: '15%' }} />
            </colgroup>
            <thead>
              <tr>
                <th style={styles.th}>Grade</th>
                <th style={styles.thNum}>n</th>
                <th style={styles.thNum}>WR %</th>
                <th style={styles.thNum}>Stop %</th>
                <th style={styles.thNum}>Mean bp</th>
                <th style={styles.thNum}>Sized (graded)</th>
                <th style={styles.thNum}>Size factor</th>
              </tr>
            </thead>
            <tbody>
              {report.grade_summary.map((g) => {
                const isTotal = g.grade === 'TOTAL';
                return (
                  <tr key={g.grade} style={isTotal ? { borderTop: `1px solid ${T.border}` } : undefined}>
                    <td style={{ ...styles.td, fontWeight: isTotal ? 700 : 500 }}>{g.grade}</td>
                    <td style={{ ...styles.tdNum, fontWeight: isTotal ? 700 : 500 }}>{fmt(g.n)}</td>
                    <td style={styles.tdNum}>{fmt1(g.wr_pct)}</td>
                    <td style={styles.tdNum}>{fmt1(g.stop_pct)}</td>
                    <td style={styles.tdNum}>{fmt2(g.mean_bp)}</td>
                    <td style={{ ...styles.tdNum, fontWeight: isTotal ? 700 : 500 }}>{fmt(g.sized_graded)}</td>
                    <td style={styles.tdNum}>{g.size_factor != null ? `x${fmt2(g.size_factor)}` : '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </ResponsiveTable>
        </>
      )}

      {Array.isArray(report.per_book) && report.per_book.length > 0 && (
        <>
          <h2 style={styles.sectionH}>Per-book breakdown</h2>
          <ResponsiveTable minWidth={640}>
            <colgroup>
              <col style={{ width: '40%' }} />
              <col style={{ width: '15%' }} />
              <col style={{ width: '15%' }} />
              <col style={{ width: '15%' }} />
              <col style={{ width: '15%' }} />
            </colgroup>
            <thead>
              <tr>
                <th style={styles.th}>Book</th>
                <th style={styles.thNum}>n</th>
                <th style={styles.thNum}>WR %</th>
                <th style={styles.thNum}>Mean bp</th>
                <th style={styles.thNum}>Net@2bp</th>
              </tr>
            </thead>
            <tbody>
              {report.per_book.map((b) => (
                <tr key={b.book}>
                  <td style={styles.td}>{b.book}</td>
                  <td style={styles.tdNum}>{fmt(b.n)}</td>
                  <td style={styles.tdNum}>{fmt1(b.wr_pct)}</td>
                  <td style={styles.tdNum}>{fmt2(b.mean_bp)}</td>
                  <td style={styles.tdNum}>{fmt2(b.net_2bp)}</td>
                </tr>
              ))}
            </tbody>
          </ResponsiveTable>
        </>
      )}

      {recon && (
        <>
          <h2 style={styles.sectionH}>Reconciliation vs locked picks CSV</h2>
          <ResponsiveTable minWidth={520}>
            <colgroup>
              <col style={{ width: '70%' }} />
              <col style={{ width: '30%' }} />
            </colgroup>
            <tbody>
              {Object.entries(recon).map(([k, v]) => (
                <tr key={k}>
                  <td style={{ ...styles.td, color: T.textDim }}>{k}</td>
                  <td style={{ ...styles.tdNum, fontWeight: 600 }}>{String(v)}</td>
                </tr>
              ))}
            </tbody>
          </ResponsiveTable>
        </>
      )}

      <AuditSection
        strategy={report.strategy}
        files={report.audit_files}
        onDownload={onDownload}
      />

      <TradesSection
        strategy={report.strategy}
        research={report.trades_research}
        deployable={report.trades_deployable}
      />
    </>
  );
}


/* ─────────────────────────────────────────────────────────────────────────────
 * Audit data — every CSV / JSON the run consumed, downloadable for review.
 * ──────────────────────────────────────────────────────────────────────────── */

function AuditSection({ strategy, files, onDownload }) {
  if (!Array.isArray(files) || files.length === 0) {
    return (
      <>
        <h2 style={styles.sectionH}>Audit data</h2>
        <div style={styles.empty}>
          No audit files in this report. Re-run the harness — older reports may pre-date the audit export.
        </div>
      </>
    );
  }

  const fmtSize = (n) => {
    if (n === null || n === undefined) return '—';
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  };

  const kindBadge = (kind) => {
    const c = kind === 'data' ? T.blue : kind === 'picks' ? T.purple : T.gold;
    return (
      <span style={{
        display: 'inline-block', padding: '2px 8px', borderRadius: 3,
        fontSize: 9, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase',
        background: `${c}22`, color: c, border: `1px solid ${c}66`,
      }}>{kind}</span>
    );
  };

  return (
    <>
      <h2 style={styles.sectionH}>Audit data</h2>
      <div style={styles.empty}>
        Inputs the {strategy} run consumed — bars, picks oracle, and the full JSON.
        Pull these to reproduce the report independently.
      </div>
      <ResponsiveTable minWidth={760}>
        <colgroup>
          <col style={{ width: '14%' }} />
          <col style={{ width: '38%' }} />
          <col style={{ width: '30%' }} />
          <col style={{ width: '10%' }} />
          <col style={{ width: '8%' }} />
        </colgroup>
        <thead>
          <tr>
            <th style={styles.th}>Kind</th>
            <th style={styles.th}>Description</th>
            <th style={styles.th}>Filename</th>
            <th style={styles.thNum}>Size</th>
            <th style={styles.th}> </th>
          </tr>
        </thead>
        <tbody>
          {files.map((f) => (
            <tr key={f.name}>
              <td style={styles.td}>{kindBadge(f.kind)}</td>
              <td style={styles.td}>{f.label}</td>
              <td style={{ ...styles.tdSmall, color: T.textDim }}>{f.name}</td>
              <td style={styles.tdNum}>{fmtSize(f.size_bytes)}</td>
              <td style={{ ...styles.td, textAlign: 'right' }}>
                <button
                  onClick={() => onDownload(f.name)}
                  disabled={!f.exists}
                  style={{
                    ...styles.csvBtn,
                    opacity: f.exists ? 1 : 0.4, cursor: f.exists ? 'pointer' : 'not-allowed',
                  }}
                  title={f.exists ? `Download ${f.name}` : 'File not on disk yet — re-run the harness'}
                >
                  ↓
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </ResponsiveTable>
    </>
  );
}


/* ─────────────────────────────────────────────────────────────────────────────
 * Trades table with mode toggle (research / deployable) + pagination + CSV.
 * ──────────────────────────────────────────────────────────────────────────── */

const PAGE_SIZE = 50;

function TradesSection({ strategy, research, deployable }) {
  const [mode, setMode] = useState('research');           // 'research' | 'deployable'
  const [page, setPage] = useState(1);
  const [sideFilter, setSideFilter] = useState('ALL');    // GBEMI only
  const [reasonFilter, setReasonFilter] = useState('ALL');

  const list = (mode === 'research' ? research : deployable) || [];

  // Available filter values
  const reasons = useMemo(() => {
    const s = new Set();
    list.forEach((t) => { if (t.exit_reason) s.add(t.exit_reason); });
    return Array.from(s).sort();
  }, [list]);

  const filtered = useMemo(() => {
    return list.filter((t) => {
      if (sideFilter !== 'ALL' && (t.side || '').toLowerCase() !== sideFilter.toLowerCase()) return false;
      if (reasonFilter !== 'ALL' && t.exit_reason !== reasonFilter) return false;
      return true;
    });
  }, [list, sideFilter, reasonFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * PAGE_SIZE;
  const visible = filtered.slice(start, start + PAGE_SIZE);

  useEffect(() => { setPage(1); }, [mode, sideFilter, reasonFilter]);

  const downloadCsv = () => {
    if (filtered.length === 0) return;
    const cols = Object.keys(filtered[0]);
    const escape = (v) => {
      if (v === null || v === undefined) return '';
      const s = String(v);
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = [cols.join(',')];
    filtered.forEach((t) => rows.push(cols.map((c) => escape(t[c])).join(',')));
    const blob = new Blob([rows.join('\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${strategy}_trades_${mode}.csv`;
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  };

  if ((research || []).length === 0 && (deployable || []).length === 0) {
    return (
      <>
        <h2 style={styles.sectionH}>Trades</h2>
        <div style={styles.empty}>
          No trades in this report. Re-run the harness — older reports may pre-date the trades-list export.
        </div>
      </>
    );
  }

  const isGbemi = strategy === 'GBEMI';

  return (
    <>
      <h2 style={styles.sectionH}>Trades</h2>

      <div style={styles.tradesControls}>
        <div style={styles.modeToggle}>
          <button
            onClick={() => setMode('research')}
            style={{
              ...styles.modeBtn,
              background: mode === 'research' ? T.blue : T.surfaceHi,
              color: mode === 'research' ? '#fff' : T.text,
            }}
          >
            Research ({(research || []).length})
          </button>
          <button
            onClick={() => setMode('deployable')}
            style={{
              ...styles.modeBtn,
              background: mode === 'deployable' ? T.blue : T.surfaceHi,
              color: mode === 'deployable' ? '#fff' : T.text,
            }}
          >
            Deployable ({(deployable || []).length})
          </button>
        </div>

        {isGbemi && (
          <label style={styles.label}>
            Side:
            <select value={sideFilter} onChange={(e) => setSideFilter(e.target.value)} style={styles.smallSelect}>
              <option value="ALL">all</option>
              <option value="long">long</option>
              <option value="short">short</option>
            </select>
          </label>
        )}

        <label style={styles.label}>
          Exit reason:
          <select value={reasonFilter} onChange={(e) => setReasonFilter(e.target.value)} style={styles.smallSelect}>
            <option value="ALL">all</option>
            {reasons.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
        </label>

        <button onClick={downloadCsv} disabled={filtered.length === 0} style={styles.csvBtn}>
          ↓ CSV ({filtered.length})
        </button>

        <span style={styles.lastFetched}>
          Showing {filtered.length === 0 ? 0 : start + 1}–{start + visible.length} of {filtered.length}
        </span>
      </div>

      <div style={{ overflowX: 'auto' }}>
        <table style={{ ...styles.table, minWidth: isGbemi ? 1700 : 920 }}>
          <thead>
            {isGbemi ? (
              <tr>
                <th style={styles.thNum}>#</th>
                <th style={styles.th}>Entry (ET)</th>
                <th style={styles.th}>DoW</th>
                <th style={styles.th}>Hour</th>
                <th style={styles.th}>Book</th>
                <th style={styles.th}>Side</th>
                <th style={styles.th}>Tier</th>
                <th style={styles.th}>Grade</th>
                <th style={styles.th}>Aligned</th>
                <th style={styles.thNum}>VIX</th>
                <th style={styles.th}>Source</th>
                <th style={styles.thNum}>Entry $</th>
                <th style={styles.th}>Exit (ET)</th>
                <th style={styles.thNum}>Exit $</th>
                <th style={styles.th}>Reason</th>
                <th style={styles.thNum}>Runup bp</th>
                <th style={styles.thNum}>Drawdown bp</th>
                <th style={styles.thNum}>PnL bp</th>
                <th style={styles.thNum}>Size×</th>
                <th style={styles.thNum}>PnL sized</th>
                <th style={styles.thNum}>Cum graded</th>
                <th style={styles.thNum}>Win</th>
              </tr>
            ) : (
              <tr>
                <th style={styles.thNum}>#</th>
                <th style={styles.th}>Entry (ET)</th>
                <th style={styles.th}>DoW</th>
                <th style={styles.th}>Hour</th>
                <th style={styles.thNum}>Entry $</th>
                <th style={styles.th}>Exit (ET)</th>
                <th style={styles.thNum}>Exit $</th>
                <th style={styles.th}>Reason</th>
                <th style={styles.thNum}>Runup bp</th>
                <th style={styles.thNum}>Drawdown bp</th>
                <th style={styles.thNum}>PnL bp</th>
                <th style={styles.thNum}>Cum bp</th>
                <th style={styles.thNum}>Win</th>
              </tr>
            )}
          </thead>
          <tbody>
            {visible.map((t, i) => isGbemi ? (
              <tr key={start + i}>
                <td style={styles.tdNum}>{start + i + 1}</td>
                <td style={styles.tdSmall}>{fmtTs(t.entry_time)}</td>
                <td style={styles.tdSmall}>{t.dow}</td>
                <td style={styles.tdSmall}>{t.hour}</td>
                <td style={styles.tdSmall}>{t.book}</td>
                <td style={{ ...styles.tdSmall, color: t.side === 'short' ? T.bear : T.bull, fontWeight: 600 }}>{t.side}</td>
                <td style={styles.tdSmall}>{t.tier}</td>
                <td style={styles.tdSmall}>{t.grade}</td>
                <td style={styles.tdSmall}>{String(!!t.aligned)}</td>
                <td style={styles.tdNum}>{t.vix != null ? fmt2(t.vix) : '—'}</td>
                <td style={{ ...styles.tdSmall, color: t.is_reentry ? T.gold : T.text }}>{t.source_algos}</td>
                <td style={styles.tdNum}>{fmt2(t.entry_price)}</td>
                <td style={styles.tdSmall}>{fmtTs(t.exit_time)}</td>
                <td style={styles.tdNum}>{fmt2(t.exit_price)}</td>
                <td style={styles.tdSmall}>{t.exit_reason}</td>
                <td style={styles.tdNum}>{fmt2(t.runup_bp)}</td>
                <td style={{ ...styles.tdNum, color: T.bear }}>{fmt2(t.drawdown_bp)}</td>
                <td style={{ ...styles.tdNum, color: (t.pnl_bp ?? 0) >= 0 ? T.bull : T.bear, fontWeight: 600 }}>
                  {fmt2(t.pnl_bp)}
                </td>
                <td style={styles.tdNum}>{t.size_factor != null ? `x${fmt2(t.size_factor)}` : '—'}</td>
                <td style={{ ...styles.tdNum, color: (t.pnl_sized_graded ?? 0) >= 0 ? T.bull : T.bear }}>
                  {fmt2(t.pnl_sized_graded)}
                </td>
                <td style={{ ...styles.tdNum, fontWeight: 600 }}>{fmt2(t.cum_graded)}</td>
                <td style={styles.tdSmall}>{t.win ? '✓' : '✗'}</td>
              </tr>
            ) : (
              <tr key={start + i}>
                <td style={styles.tdNum}>{start + i + 1}</td>
                <td style={styles.tdSmall}>{fmtTs(t.entry_time)}</td>
                <td style={styles.tdSmall}>{t.dow}</td>
                <td style={styles.tdSmall}>{t.hour}</td>
                <td style={styles.tdNum}>{fmt2(t.entry_price)}</td>
                <td style={styles.tdSmall}>{fmtTs(t.exit_time)}</td>
                <td style={styles.tdNum}>{fmt2(t.exit_price)}</td>
                <td style={styles.tdSmall}>{t.exit_reason}</td>
                <td style={styles.tdNum}>{fmt2(t.runup_bp)}</td>
                <td style={{ ...styles.tdNum, color: T.bear }}>{fmt2(t.drawdown_bp)}</td>
                <td style={{ ...styles.tdNum, color: (t.pnl_bp ?? 0) >= 0 ? T.bull : T.bear, fontWeight: 600 }}>
                  {fmt2(t.pnl_bp)}
                </td>
                <td style={{ ...styles.tdNum, fontWeight: 600 }}>{fmt2(t.cum_pnl_bp)}</td>
                <td style={styles.tdSmall}>{t.win ? '✓' : '✗'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div style={styles.pager}>
          <button onClick={() => setPage(1)}                  disabled={safePage <= 1}            style={styles.pagerBtn}>« First</button>
          <button onClick={() => setPage((p) => p - 1)}       disabled={safePage <= 1}            style={styles.pagerBtn}>‹ Prev</button>
          <span style={styles.pagerLabel}>Page {safePage} of {totalPages}</span>
          <button onClick={() => setPage((p) => p + 1)}       disabled={safePage >= totalPages}   style={styles.pagerBtn}>Next ›</button>
          <button onClick={() => setPage(totalPages)}         disabled={safePage >= totalPages}   style={styles.pagerBtn}>Last »</button>
        </div>
      )}
    </>
  );
}


function fmtTs(s) {
  if (!s) return '—';
  // Trim microseconds / timezone for compact display: "2025-09-02 10:27"
  const str = String(s);
  const m = str.match(/^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2})/);
  return m ? `${m[1]} ${m[2]}` : str.slice(0, 16);
}


function Tr({ k, v, cert }) {
  return (
    <tr>
      <td style={styles.td}>{k}</td>
      <td style={styles.tdNum}>{v}</td>
      <td style={{ ...styles.tdNum, color: T.textDim }}>
        {cert !== undefined && cert !== null ? String(cert) : '—'}
      </td>
    </tr>
  );
}


const styles = {
  page: {
    // .app is height:100vh + overflow:hidden — we need to claim the remaining
    // height and own the vertical scroll, otherwise content past the viewport
    // is clipped (no scrollbar appears).
    flex: 1, minHeight: 0, overflowY: 'auto',
    padding: 'clamp(12px, 3vw, 24px) clamp(10px, 4vw, 32px)',
    background: T.bg, color: T.text, fontFamily: "'Inter', sans-serif",
    overflowX: 'hidden',
  },
  header: { marginBottom: 16 },
  title: { fontSize: 22, margin: '0 0 4px', fontWeight: 700 },
  subtitle: { color: T.textDim, fontSize: 12 },
  controls: {
    display: 'flex', gap: 12, alignItems: 'center', marginBottom: 10,
    padding: 12, background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6,
    flexWrap: 'wrap',
  },
  runControls: {
    display: 'flex', gap: 12, alignItems: 'center', marginBottom: 18,
    padding: '10px 12px', background: T.surfaceHi,
    border: `1px dashed ${T.border}`, borderRadius: 6,
    flexWrap: 'wrap',
  },
  runLabel: {
    fontSize: 11, fontWeight: 600, color: T.textDim,
    textTransform: 'uppercase', letterSpacing: '0.06em',
  },
  dtInput: {
    background: T.surface, color: T.text, border: `1px solid ${T.border}`,
    borderRadius: 4, padding: '5px 8px', fontSize: 12, fontFamily: 'monospace',
  },
  runBtn: {
    background: T.bull, color: '#fff', border: 'none',
    borderRadius: 4, padding: '7px 16px', fontSize: 12, fontWeight: 700,
    cursor: 'pointer', letterSpacing: '0.04em',
  },
  runBtnRunning: {
    background: T.gold, color: '#fff', border: 'none',
    borderRadius: 4, padding: '7px 16px', fontSize: 12, fontWeight: 700,
    cursor: 'wait', letterSpacing: '0.04em',
  },
  label: { fontSize: 12, color: T.textDim, display: 'flex', gap: 8, alignItems: 'center' },
  select: {
    background: T.surfaceHi, color: T.text, border: `1px solid ${T.border}`,
    borderRadius: 4, padding: '5px 10px', fontSize: 12, minWidth: 0,
    width: 'min(100%, 520px)',
  },
  refreshBtn: {
    background: T.blue, color: '#fff', border: 'none',
    borderRadius: 4, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
  },
  lastFetched: { color: T.textMuted, fontSize: 11 },
  error: { color: T.bear, fontSize: 12 },
  empty: { color: T.textDim, fontSize: 13, padding: 24 },
  headerCard: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    gap: 12, flexWrap: 'wrap',
    padding: '14px 16px',
    background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6,
    marginBottom: 12,
  },
  strategyTag: {
    display: 'inline-block',
    fontSize: 18, fontWeight: 800, letterSpacing: '0.04em',
    color: T.blue, marginBottom: 6,
  },
  cardTitle: { fontSize: 13, color: T.textDim, marginBottom: 8 },
  metaLine: { fontSize: 11, color: T.textDim, marginBottom: 2 },
  mono: { fontFamily: 'monospace', color: T.text },
  contextNote: {
    fontSize: 11, color: T.textDim, padding: '8px 12px',
    background: T.surfaceHi, border: `1px dashed ${T.borderLight}`, borderRadius: 4,
    marginBottom: 16,
  },
  downloadBtn: {
    background: T.bull, color: '#fff', border: 'none',
    borderRadius: 4, padding: '8px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer',
  },
  sectionH: { fontSize: 14, color: T.textDim, margin: '20px 0 8px', fontWeight: 600 },
  table: {
    width: '100%', borderCollapse: 'collapse', fontSize: 12,
    background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6, overflow: 'hidden',
    tableLayout: 'fixed',   // honour the colgroup widths
  },
  tableScroll: {
    width: '100%', maxWidth: '100%', overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
  },
  th: {
    textAlign: 'left', padding: '8px 10px',
    background: T.surfaceHi, color: T.textDim,
    fontWeight: 600, fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase',
    borderBottom: `1px solid ${T.border}`,
  },
  thNum: {
    textAlign: 'right', padding: '8px 10px',
    background: T.surfaceHi, color: T.textDim,
    fontWeight: 600, fontSize: 11, letterSpacing: '0.04em', textTransform: 'uppercase',
    borderBottom: `1px solid ${T.border}`,
  },
  td: {
    padding: '6px 10px', borderBottom: `1px solid ${T.borderLight}`,
    overflow: 'hidden', textOverflow: 'ellipsis',
  },
  tdSmall: {
    padding: '5px 8px', borderBottom: `1px solid ${T.borderLight}`,
    fontFamily: 'monospace', fontSize: 11,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  tdNum: {
    padding: '6px 10px', borderBottom: `1px solid ${T.borderLight}`,
    fontFamily: 'monospace', textAlign: 'right',
    overflow: 'hidden', textOverflow: 'ellipsis',
  },
  tradesControls: {
    display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
    padding: '10px 12px', marginBottom: 10,
    background: T.surface, border: `1px solid ${T.border}`, borderRadius: 6,
  },
  modeToggle: { display: 'flex', gap: 2, border: `1px solid ${T.border}`, borderRadius: 4, overflow: 'hidden' },
  modeBtn: {
    padding: '6px 12px', border: 'none', cursor: 'pointer',
    fontSize: 11, fontWeight: 600,
  },
  smallSelect: {
    background: T.surfaceHi, color: T.text, border: `1px solid ${T.border}`,
    borderRadius: 4, padding: '4px 8px', fontSize: 11,
  },
  csvBtn: {
    background: T.surfaceHi, color: T.text, border: `1px solid ${T.border}`,
    borderRadius: 4, padding: '5px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer',
  },
  pager: {
    display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center',
    marginTop: 10, fontSize: 12,
  },
  pagerBtn: {
    background: T.surfaceHi, color: T.text, border: `1px solid ${T.border}`,
    borderRadius: 4, padding: '4px 10px', fontSize: 11, cursor: 'pointer',
  },
  pagerLabel: { color: T.textDim, fontSize: 12 },
};
